import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  LinearProgress,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';

function CategoryDetails() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [training, setTraining] = useState(false);
  const [error, setError] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => {
    fetchCategoryAndDocuments();
  }, [categoryId]);

  const fetchCategoryAndDocuments = async () => {
    try {
      setLoading(true);
      const [catResponse, docsResponse] = await Promise.all([
        apiService.getCustomCategories(),
        apiService.getCategoryDocuments(categoryId)
      ]);
      
      const cat = catResponse.data.categories.find(c => c.id === categoryId);
      setCategory(cat);
      setDocuments(docsResponse.data.documents || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      for (const file of files) {
        await apiService.uploadCustomDocument(categoryId, file);
      }
      fetchCategoryAndDocuments();
    } catch (err) {
      console.error('Error uploading files:', err);
      setError('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await apiService.deleteCustomDocument(docId);
      fetchCategoryAndDocuments();
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    }
  };

  const handleToggleDocument = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } else {
      setSelectedDocs([...selectedDocs, docId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(doc => doc.id));
    }
  };

  const handleTrainModel = async () => {
    if (selectedDocs.length === 0) {
      setError('Please select at least one document to train');
      return;
    }

    if (selectedDocs.length > 100) {
      setError('Maximum 100 documents allowed for training');
      return;
    }

    if (!window.confirm(`Train AI model with ${selectedDocs.length} selected documents? This may take several minutes.`)) {
      return;
    }

    setTraining(true);
    setError('');

    try {
      await apiService.trainCustomModel(categoryId, selectedDocs);
      alert('Model trained successfully!');
      fetchCategoryAndDocuments();
      setSelectedDocs([]);
    } catch (err) {
      console.error('Error training model:', err);
      setError(err.response?.data?.error || 'Failed to train model');
    } finally {
      setTraining(false);
    }
  };

  const handleViewDocument = (doc) => {
    setViewingDoc(doc);
    setViewerOpen(true);
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <PictureAsPdfIcon color="error" />;
    if (['doc', 'docx'].includes(fileType)) return <DescriptionIcon color="primary" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileType)) return <ImageIcon color="success" />;
    if (fileType === 'txt') return <TextFieldsIcon color="action" />;
    return <DescriptionIcon />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!category) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary">
          Category not found
        </Typography>
        <Button onClick={() => navigate('/custom-models')} sx={{ mt: 2 }}>
          Back to Custom Models
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/custom-models')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {category.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {category.description || 'No description'}
          </Typography>
        </Box>
        {category.rag_trained && (
          <Chip
            label={`Trained (${category.num_chunks || 0} chunks)`}
            color="success"
            icon={<ModelTrainingIcon />}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Upload and Training Section */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Documents ({documents.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <input
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp"
                    style={{ display: 'none' }}
                    id="file-upload"
                    multiple
                    type="file"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      disabled={uploading}
                    >
                      Upload Documents
                    </Button>
                  </label>
                  <Button
                    variant="contained"
                    startIcon={<ModelTrainingIcon />}
                    onClick={handleTrainModel}
                    disabled={training || selectedDocs.length === 0}
                  >
                    {training ? 'Training...' : `Train AI Model (${selectedDocs.length})`}
                  </Button>
                </Box>
              </Box>

              {uploading && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Uploading documents...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}

              {training && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info">
                    Training AI model... This may take 10-30 minutes depending on the number of documents.
                    You can navigate away from this page.
                  </Alert>
                  <LinearProgress sx={{ mt: 2 }} />
                </Box>
              )}

              {documents.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <UploadFileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Documents Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Upload PDF, Word, Image, or Text files to train your custom AI model
                  </Typography>
                  <label htmlFor="file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      disabled={uploading}
                    >
                      Upload First Document
                    </Button>
                  </label>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Button size="small" onClick={handleSelectAll}>
                      {selectedDocs.length === documents.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedDocs.length > 0 && (
                      <Typography variant="body2" color="primary">
                        {selectedDocs.length} selected (Max: 100)
                      </Typography>
                    )}
                  </Box>
                  <List>
                    {documents.map((doc) => (
                      <ListItem
                        key={doc.id}
                        sx={{
                          bgcolor: selectedDocs.includes(doc.id) ? 'primary.light' : 'grey.50',
                          borderRadius: 1,
                          mb: 1,
                          border: selectedDocs.includes(doc.id) ? '2px solid' : '1px solid',
                          borderColor: selectedDocs.includes(doc.id) ? 'primary.main' : 'divider'
                        }}
                      >
                        <Checkbox
                          checked={selectedDocs.includes(doc.id)}
                          onChange={() => handleToggleDocument(doc.id)}
                          disabled={training}
                        />
                        <ListItemIcon>
                          {getFileIcon(doc.file_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.filename}
                          secondary={
                            <>
                              {formatFileSize(doc.file_size)} • {' '}
                              {doc.uploaded_at?.seconds 
                                ? new Date(doc.uploaded_at.seconds * 1000).toLocaleDateString()
                                : 'Unknown date'}
                            </>
                          }
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDocument(doc)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDocument(doc.id)}
                            disabled={training}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Document Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {viewingDoc?.filename}
        </DialogTitle>
        <DialogContent>
          {viewingDoc && (
            <Box sx={{ minHeight: '500px' }}>
              {viewingDoc.file_type === 'pdf' && (
                <iframe
                  src={viewingDoc.file_url}
                  width="100%"
                  height="600px"
                  title={viewingDoc.filename}
                  style={{ border: 'none' }}
                />
              )}
              {['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(viewingDoc.file_type) && (
                <img
                  src={viewingDoc.file_url}
                  alt={viewingDoc.filename}
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              )}
              {['doc', 'docx'].includes(viewingDoc.file_type) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Word documents cannot be previewed in browser
                  </Typography>
                  <Button
                    variant="contained"
                    href={viewingDoc.file_url}
                    target="_blank"
                    sx={{ mt: 2 }}
                  >
                    Download Document
                  </Button>
                </Box>
              )}
              {viewingDoc.file_type === 'txt' && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '600px', overflow: 'auto' }}>
                  <iframe
                    src={viewingDoc.file_url}
                    width="100%"
                    height="600px"
                    title={viewingDoc.filename}
                    style={{ border: 'none' }}
                  />
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewerOpen(false)}>Close</Button>
          <Button
            variant="contained"
            href={viewingDoc?.file_url}
            target="_blank"
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoryDetails;
